/**
 * AttendeeDetailsScreen — loads real attendee profile from API
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  ArrowLeft, UserPlus, Linkedin, Globe,
  Building2, Sparkles, ChevronRight, Loader2, User as UserIcon, Flag, UserX,
} from 'lucide-react';
import { notify } from '../utils/notifications';
import { AvatarImage } from '../components/AvatarImage';
import { useApp } from '../context/AppContext';
import { useEventData } from '../context/EventDataContext';
import { useFavorites } from '../hooks/useFavorites';
import { CopilotFab } from '../components/CopilotFab';
import { useEventModules } from '../hooks/useEventModules';
import { ReportContentDialog } from '../components/ReportContentDialog';
import { safetyService } from '../services/safetyService';
import type { ProfileReportCategory } from '../services/safetyService';

interface AttendeeProfile {
  id: string;
  name: string;
  title?: string | null;
  company?: string | null;
  bio?: string | null;
  avatar_url?: string | null;
  linkedin?: string | null;
  portfolio?: string | null;
  interests?: string[] | null;
  research_interests?: string | null;
}

// ─── AI Icebreaker Card ─────────────────────────────────────────────────────

function AIIcebreakerCard({
  attendee,
  onFullBrief,
}: {
  attendee: AttendeeProfile;
  onFullBrief: () => void;
}) {
  const interests = attendee.interests ?? [];
  const company = attendee.company ?? 'their organization';

  const icebreakers = [
    interests.length > 0
      ? `"I see you work at ${company}${interests[0] ? ` and have interest in ${interests[0]}` : ''} — what's been the most exciting thing you've seen at the conference so far?"`
      : `"What's been your highlight of the conference so far?"`,
    interests.length > 1
      ? `"It looks like we share an interest in ${interests.slice(0, 2).join(' and ')} — have you attended any sessions on that?"`
      : `"What sessions are you most looking forward to?"`,
    `"What are you hoping to take away or apply from your time here?"`,
  ];

  return (
    <div
      className="rounded-xl border overflow-hidden mb-5"
      style={{ background: 'var(--app-tag-bg)', borderColor: 'var(--app-tag-border)' }}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--app-tag-border)' }}>
        <div className="flex items-center gap-2">
          <div className="size-6 rounded-full flex items-center justify-center" style={{ background: 'var(--app-accent-dim)' }}>
            <Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--app-accent)' }} />
          </div>
          <span className="text-xs" style={{ color: 'var(--app-accent)' }}>AI Networking Suggestions</span>
        </div>
      </div>

      <div className="px-4 py-3">
        <p className="text-[10px] uppercase tracking-wider mb-2.5" style={{ color: 'var(--app-text-muted)' }}>
          Conversation starters
        </p>
        <div className="flex flex-col gap-2.5">
          {icebreakers.map((ib, i) => (
            <p key={i} className="text-xs leading-relaxed italic" style={{ color: 'var(--app-text-subtle)' }}>
              {ib}
            </p>
          ))}
        </div>
      </div>

      <button
        onClick={onFullBrief}
        className="w-full flex items-center justify-between px-4 py-3 border-t transition-colors active:opacity-70"
        style={{ borderColor: 'var(--app-tag-border)' }}
      >
        <span className="text-xs" style={{ color: 'var(--app-accent)' }}>Get full AI networking brief</span>
        <ChevronRight className="w-3.5 h-3.5" style={{ color: 'var(--app-accent)' }} />
      </button>
    </div>
  );
}

// ─── Main Screen ─────────────────────────────────────────────────────────────

export default function AttendeeDetailsScreen() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { aiPreferences, currentEventId, authUser } = useApp();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { isEnabled } = useEventModules();
  const { preview, services: { attendeeService } } = useEventData();

  const [attendee, setAttendee] = useState<AttendeeProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [errorMessage, setErrorMessage] = useState('Attendee not found');
  const [blocked, setBlocked] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [safetyBusy, setSafetyBusy] = useState(false);
  const [safetyError, setSafetyError] = useState('');
  const [safetyNotice, setSafetyNotice] = useState('');
  const safetyScope = `${authUser?.id ?? ''}:${currentEventId ?? ''}:${id ?? ''}`;
  const safetyScopeRef = useRef(safetyScope);
  safetyScopeRef.current = safetyScope;

  const saved = attendee ? isFavorite(attendee.id, 'attendee') : false;

  useEffect(() => {
    let cancelled = false;
    setAttendee(null);
    setNotFound(false);
    setErrorMessage('Attendee not found');
    setBlocked(false);
    setReportOpen(false);
    setSafetyBusy(false);
    setSafetyError('');
    setSafetyNotice('');
    setLoading(true);
    if (!id) { setNotFound(true); setLoading(false); return; }
    if (!currentEventId) {
      setErrorMessage('Select an event before viewing attendee profiles.');
      setNotFound(true);
      setLoading(false);
      return;
    }
    const profile = attendeeService.getById(id, currentEventId);
    const blocks = authUser?.id
      ? safetyService.listBlocks(currentEventId)
      : Promise.resolve([]);
    Promise.allSettled([profile, blocks])
      .then(([profileResult, blocksResult]) => {
        if (cancelled) return;
        const isBlocked = blocksResult.status === 'fulfilled'
          && blocksResult.value.some((item) => item.user_id === id);
        setBlocked(isBlocked);
        if (profileResult.status === 'fulfilled' && profileResult.value) {
          setAttendee(profileResult.value as unknown as AttendeeProfile);
        } else if (!isBlocked) {
          const error = profileResult.status === 'rejected' ? profileResult.reason : null;
          setErrorMessage(error instanceof Error ? error.message : 'Attendee not found');
          setNotFound(true);
        }
        if (blocksResult.status === 'rejected' && profileResult.status === 'fulfilled') {
          setSafetyError('Unable to check blocked attendees. Try again later.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, currentEventId, attendeeService, authUser?.id]);

  const handleBlock = async () => {
    if (!id || !currentEventId || safetyBusy) return;
    if (!window.confirm('Block this attendee? You will no longer see each other in attendee discovery.')) return;
    const requestScope = safetyScope;
    setSafetyBusy(true);
    setSafetyError('');
    try {
      await safetyService.blockUser(currentEventId, id, {});
      if (safetyScopeRef.current !== requestScope) return;
      setBlocked(true);
      setSafetyNotice('Attendee blocked. You can unblock them here.');
    } catch (error) {
      if (safetyScopeRef.current === requestScope) {
        setSafetyError(error instanceof Error ? error.message : 'Unable to block attendee. Try again.');
      }
    } finally {
      if (safetyScopeRef.current === requestScope) setSafetyBusy(false);
    }
  };

  const handleUnblock = async () => {
    if (!id || !currentEventId || safetyBusy) return;
    const requestScope = safetyScope;
    setSafetyBusy(true);
    setSafetyError('');
    try {
      await safetyService.unblockUser(currentEventId, id, {});
      if (safetyScopeRef.current !== requestScope) return;
      setBlocked(false);
      const profile = await attendeeService.getById(id, currentEventId);
      if (safetyScopeRef.current !== requestScope) return;
      if (profile) setAttendee(profile as unknown as AttendeeProfile);
      else setNotFound(true);
      setSafetyNotice('Attendee unblocked.');
    } catch (error) {
      if (safetyScopeRef.current === requestScope) {
        setSafetyError(error instanceof Error ? error.message : 'Unable to unblock attendee. Try again.');
      }
    } finally {
      if (safetyScopeRef.current === requestScope) setSafetyBusy(false);
    }
  };

  const handleReport = async (category: string, details: string) => {
    if (!id || !currentEventId) throw new Error('Select an event to report this profile.');
    const requestScope = safetyScope;
    const acknowledgement = await safetyService.reportProfile(
      currentEventId, id, category as ProfileReportCategory, details, {},
    );
    if (safetyScopeRef.current !== requestScope) return;
    setSafetyNotice(acknowledgement.already_reported
      ? 'This profile was already reported. Our safety team has it.'
      : 'Report submitted. Our safety team will review it.');
    setSafetyError('');
  };

  if (loading) {
    return (
      <div className="size-full flex items-center justify-center" style={{ background: 'var(--app-bg)' }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--app-accent)' }} />
      </div>
    );
  }

  if (blocked) {
    return (
      <div className="size-full flex flex-col items-center justify-center gap-4 px-6 text-center" style={{ background: 'var(--app-bg)' }}>
        <UserX className="w-12 h-12" style={{ color: 'var(--app-text-subtle)' }} />
        <h1 className="text-xl" style={{ color: 'var(--app-text)' }}>You blocked this attendee</h1>
        <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>
          You will not see each other in attendee discovery.
        </p>
        {safetyNotice && <p role="status" className="text-sm" style={{ color: 'var(--app-text-muted)' }}>{safetyNotice}</p>}
        {safetyError && <p role="alert" className="text-sm text-red-400">{safetyError}</p>}
        <button
          onClick={handleUnblock}
          disabled={safetyBusy}
          className="min-h-11 rounded-xl px-5 text-sm disabled:opacity-50"
          style={{ background: 'var(--app-accent-solid)', color: '#fff' }}
        >
          {safetyBusy ? 'Unblocking…' : 'Unblock attendee'}
        </button>
        <button onClick={() => navigate(-1)} className="text-sm" style={{ color: 'var(--app-accent)' }}>
          Go back
        </button>
      </div>
    );
  }

  if (notFound || !attendee) {
    return (
      <div className="size-full flex flex-col items-center justify-center gap-4" style={{ background: 'var(--app-bg)' }}>
        <UserIcon className="w-12 h-12" style={{ color: 'var(--app-text-subtle)' }} />
        <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>{errorMessage}</p>
        <button onClick={() => navigate(-1)} className="text-sm" style={{ color: 'var(--app-accent)' }}>Go back</button>
      </div>
    );
  }

  const handleConnect = () => {
    toggleFavorite(attendee.id, 'attendee');
    if (!saved) notify.custom.contactAdded(attendee.name);
    else notify.info('Removed from contacts');
  };

  const handleFullBrief = () => {
    const params = new URLSearchParams({
      mode: 'networking',
      attendeeId: attendee.id,
      attendeeName: attendee.name,
      ...(attendee.title && { attendeeTitle: attendee.title }),
      ...(attendee.company && { attendeeCompany: attendee.company }),
    });
    if (attendee.interests) attendee.interests.forEach(i => params.append('attendeeInterests', i));
    navigate(`/report-builder?${params.toString()}`);
  };

  return (
    <div className="size-full flex flex-col overflow-hidden" style={{ background: 'var(--app-bg)' }}>
      {/* Hero */}
      <div className="relative h-48 shrink-0">
        <AvatarImage source={attendee.avatar_url} alt={attendee.name} className="w-full h-full object-cover object-top" fallback={
          <div className="w-full h-full flex items-center justify-center" style={{ background: 'var(--app-surface)' }}>
            <UserIcon className="w-16 h-16" style={{ color: 'var(--app-text-subtle)' }} />
          </div>
        } />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), var(--app-bg))' }} />
        <button
          aria-label="Back"
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 size-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center active:scale-90 transition-transform"
        >
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto -mt-4">
        <div className="px-4 pt-6 pb-8">
          <h1 className="text-[var(--app-text)] text-2xl mb-1">{attendee.name}</h1>
          {attendee.title && <p className="text-[var(--app-text-muted)] text-sm">{attendee.title}</p>}
          {attendee.company && (
            <div className="flex items-center gap-1.5 mt-1 text-sm" style={{ color: 'var(--app-text-subtle)' }}>
              <Building2 className="w-3.5 h-3.5" />
              <span>{attendee.company}</span>
            </div>
          )}

          {/* Actions */}
          {!preview && <div className="flex gap-3 mt-5 mb-5">
            <button
              onClick={handleConnect}
              className="flex-1 h-11 rounded-xl flex items-center justify-center gap-2 text-sm transition-colors active:scale-[0.98]"
              style={saved
                ? { background: 'var(--app-accent-solid)', color: '#fff' }
                : { background: 'var(--app-surface)', color: 'var(--app-text)', border: '1px solid var(--app-border)' }
              }
            >
              <UserPlus className="w-4 h-4" />
              {saved ? 'Connected' : 'Connect'}
            </button>
          </div>}

          {!preview && authUser?.id !== attendee.id && (
            <div className="mb-5 flex flex-wrap gap-3">
              <button
                onClick={() => setReportOpen(true)}
                className="min-h-11 rounded-xl border px-4 text-sm flex items-center gap-2"
                style={{ borderColor: 'var(--app-border)', color: 'var(--app-text-muted)' }}
              >
                <Flag className="w-4 h-4" /> Report profile
              </button>
              <button
                onClick={handleBlock}
                disabled={safetyBusy}
                className="min-h-11 rounded-xl border px-4 text-sm flex items-center gap-2 disabled:opacity-50"
                style={{ borderColor: 'var(--app-border)', color: 'var(--app-text-muted)' }}
              >
                <UserX className="w-4 h-4" /> Block attendee
              </button>
            </div>
          )}
          {safetyNotice && <p role="status" className="mb-4 text-sm" style={{ color: 'var(--app-text-muted)' }}>{safetyNotice}</p>}
          {safetyError && <p role="alert" className="mb-4 text-sm text-red-400">{safetyError}</p>}

          {/* Social Links */}
          {(attendee.linkedin || attendee.portfolio) && (
            <div className="flex gap-3 mb-5">
              {attendee.linkedin && (
                <button
                  onClick={() => window.open(attendee.linkedin!.startsWith('http') ? attendee.linkedin! : `https://${attendee.linkedin}`, '_blank')}
                  className="flex-1 h-10 rounded-xl border flex items-center justify-center gap-1.5 text-xs active:scale-[0.98]"
                  style={{ background: 'var(--app-surface)', color: 'var(--app-text-subtle)', borderColor: 'var(--app-border)' }}
                >
                  <Linkedin className="w-3.5 h-3.5" />
                  LinkedIn
                </button>
              )}
              {attendee.portfolio && (
                <button
                  onClick={() => window.open(attendee.portfolio!.startsWith('http') ? attendee.portfolio! : `https://${attendee.portfolio}`, '_blank')}
                  className="flex-1 h-10 rounded-xl border flex items-center justify-center gap-1.5 text-xs active:scale-[0.98]"
                  style={{ background: 'var(--app-surface)', color: 'var(--app-text-subtle)', borderColor: 'var(--app-border)' }}
                >
                  <Globe className="w-3.5 h-3.5" />
                  Portfolio
                </button>
              )}
            </div>
          )}

          {/* AI Icebreaker — gated by aiSuggestions pref */}
          {!preview && isEnabled('ai_copilot') && aiPreferences.aiSuggestions && (
            <AIIcebreakerCard attendee={attendee} onFullBrief={handleFullBrief} />
          )}

          {/* Bio */}
          {attendee.bio && (
            <div className="mb-5">
              <h2 className="text-[var(--app-text)] text-base mb-2">About</h2>
              <p className="text-[var(--app-text-muted)] text-sm leading-relaxed">{attendee.bio}</p>
            </div>
          )}

          {/* Research Interests */}
          {attendee.research_interests && (
            <div className="mb-5">
              <h2 className="text-[var(--app-text)] text-base mb-2">Research Interests</h2>
              <p className="text-[var(--app-text-muted)] text-sm leading-relaxed">{attendee.research_interests}</p>
            </div>
          )}

          {/* Interest Tags */}
          {attendee.interests && attendee.interests.length > 0 && (
            <div className="mb-5">
              <h2 className="text-[var(--app-text)] text-base mb-3">Interests</h2>
              <div className="flex flex-wrap gap-2">
                {attendee.interests.map(interest => (
                  <span
                    key={interest}
                    className="text-xs px-3 py-1.5 rounded-full border"
                    style={{ background: 'var(--app-tag-bg)', color: 'var(--app-accent)', borderColor: 'var(--app-tag-border)' }}
                  >
                    {interest}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Full Networking Brief CTA */}
          {!preview && isEnabled('ai_copilot') && (
          <button
            onClick={handleFullBrief}
            className="w-full h-12 rounded-xl flex items-center justify-center gap-2 text-sm border active:scale-[0.98] transition-transform"
            style={{ background: 'var(--app-tag-bg)', borderColor: 'var(--app-tag-border)', color: 'var(--app-accent)' }}
          >
            <Sparkles className="w-4 h-4" />
            Get AI Networking Brief
          </button>
          )}
        </div>
      </div>
      {!preview && <CopilotFab />}
      <ReportContentDialog
        key={`${currentEventId ?? ''}:${attendee.id}`}
        open={reportOpen}
        onOpenChange={setReportOpen}
        title="Report profile"
        categories={[
          { value: 'impersonation', label: 'Impersonation' },
          { value: 'harassment', label: 'Harassment' },
          { value: 'inappropriate_content', label: 'Inappropriate content' },
          { value: 'spam', label: 'Spam' },
          { value: 'privacy_concern', label: 'Privacy concern' },
          { value: 'other', label: 'Other' },
        ]}
        onSubmit={handleReport}
      />
    </div>
  );
}
